import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getUserData, updateUser, requestPasswordReset } from '../../api/user';
import { useNotifications } from '../../hooks/useNotifications';
import { t } from '../../utils/translations';
import { accountManagementStyles } from './AccountManagement.styles';

const AccountManagementForm: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showError, showSuccess } = useNotifications();

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await getUserData();
        setFirstName(userData.firstName);
        setLastName(userData.lastName);
        setUserEmail(userData.email);
      } catch (error) {
        showError(error instanceof Error ? error.message : t('user.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateFirstName = (value: string): string | null => {
    if (!value.trim()) return t('accountManagement.firstNameRequired');
    if (value.length < 2) return t('accountManagement.firstNameMinLength');
    if (value.length > 200) return t('accountManagement.firstNameMaxLength');
    return null;
  };

  const validateLastName = (value: string): string | null => {
    if (!value.trim()) return t('accountManagement.lastNameRequired');
    if (value.length < 2) return t('accountManagement.lastNameMinLength');
    if (value.length > 200) return t('accountManagement.lastNameMaxLength');
    return null;
  };

  const firstNameError = touched.firstName ? validateFirstName(firstName) : null;
  const lastNameError = touched.lastName ? validateLastName(lastName) : null;

  const isFormValid = !validateFirstName(firstName) && !validateLastName(lastName);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setTouched({
      firstName: true,
      lastName: true,
    });

    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      await updateUser({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      showSuccess(t('user.updateSuccess'));
    } catch (error) {
      showError(error instanceof Error ? error.message : t('user.updateFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!userEmail) {
      showError(t('user.updateFailed'));
      return;
    }

    if (!executeRecaptcha) {
      showError(t('auth.recaptchaFailed'), t('common.error'));
      return;
    }

    setIsRequestingReset(true);

    try {
      const recaptchaToken = await executeRecaptcha('change_password');

      await requestPasswordReset({ email: userEmail, recaptchaToken });
      showSuccess(t('user.passwordResetSuccess'));
    } catch (error) {
      showError(error instanceof Error ? error.message : t('user.passwordResetFailed'));
    } finally {
      setIsRequestingReset(false);
    }
  };

  const handleDeleteAccountClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={accountManagementStyles.container}>
        <Box sx={accountManagementStyles.loadingContainer}>
          <CircularProgress />
          <Typography variant="body1" sx={accountManagementStyles.loadingText}>
            {t('common.loading')}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={accountManagementStyles.container}>
      <Paper elevation={3} sx={accountManagementStyles.paper}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('accountManagement.title')}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={accountManagementStyles.form}>
          <Typography variant="h6" gutterBottom>
            {t('accountManagement.editData')}
          </Typography>

          <TextField
            fullWidth
            label={t('accountManagement.firstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => setTouched({ ...touched, firstName: true })}
            error={!!firstNameError}
            helperText={firstNameError}
            margin="normal"
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            label={t('accountManagement.lastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={() => setTouched({ ...touched, lastName: true })}
            error={!!lastNameError}
            helperText={lastNameError}
            margin="normal"
            disabled={isSubmitting}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting || !isFormValid}
            sx={accountManagementStyles.button}
          >
            {isSubmitting ? t('common.saving') : t('accountManagement.saveChanges')}
          </Button>

          <Divider sx={accountManagementStyles.divider} />

          <Typography variant="h6" gutterBottom>
            {t('accountManagement.changePassword')}
          </Typography>

          <Alert severity="info" sx={accountManagementStyles.alert}>
            {t('accountManagement.changePasswordDescription')}
          </Alert>

          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={handlePasswordReset}
            disabled={isRequestingReset}
            sx={accountManagementStyles.button}
          >
            {isRequestingReset ? t('common.loading') : t('accountManagement.changePassword')}
          </Button>

          <Divider sx={accountManagementStyles.divider} />

          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={handleDeleteAccountClick}
            sx={accountManagementStyles.button}
          >
            {t('accountManagement.deleteAccount')}
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>{t('accountManagement.deleteAccountTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('accountManagement.deleteAccountMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary" autoFocus>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export const AccountManagement: React.FC = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

  if (!recaptchaSiteKey) {
    return (
      <Container>
        <Typography color="error">
          {t('auth.recaptchaNotConfigured')}
        </Typography>
      </Container>
    );
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <AccountManagementForm />
    </GoogleReCaptchaProvider>
  );
};
